import type { QueryClient } from '@tanstack/react-query';

import type { Booking, BookingStatus } from '../api/schemas';
import { CHARGE_LOOKBACK_DAYS } from './bookingWindows';
import { isSameDay } from './format';

/**
 * Writes a mutation's returned booking straight into the caches. The backend
 * can serve reads a few seconds stale right after a write, so an immediate
 * refetch resurrects the old status (the tap looks like it did nothing and a
 * second tap 409s); the mutation response is the truth, and the periodic
 * refetches reconcile later.
 */
export function applyBookingToCaches(queryClient: QueryClient, booking: Booking): void {
  queryClient.setQueriesData<{ data: Booking[] }>({ queryKey: ['business-bookings'] }, (old) =>
    old ? { ...old, data: old.data.map((entry) => (entry.id === booking.id ? booking : entry)) } : old,
  );
  queryClient.setQueryData<Record<string, unknown>>(['business-booking', booking.id], (old) =>
    old ? { ...old, ...booking } : old,
  );
}

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  held: 'Apartada',
  confirmed: 'Confirmada',
  completed: 'Completada',
  paid: 'Pagada',
  released: 'Liberada',
  cancelled: 'Cancelada',
  no_show: 'No llegó',
};

/** Maps each status to the typographic status-word tone in global.css. */
export const BOOKING_STATUS_TONE: Record<BookingStatus, 'hold' | 'good' | 'due' | 'off'> = {
  held: 'hold',
  confirmed: 'good',
  completed: 'due',
  paid: 'good',
  released: 'off',
  cancelled: 'off',
  no_show: 'off',
};

export type AgendaDay = {
  /** Local calendar key, stable for React keys. */
  key: string;
  bookings: Booking[];
};

export type AgendaGroups = {
  /** Confirmed visits already past their start: the ones asking to be closed. */
  porCerrar: Booking[];
  /** The rest of today, in time order: upcoming holds and settled visits alike. */
  hoy: Booking[];
  /** Future days in order. Released/cancelled never appear: Historial owns them. */
  proximas: AgendaDay[];
};

const AGENDA_HIDDEN: ReadonlySet<BookingStatus> = new Set(['released', 'cancelled', 'no_show']);

/**
 * One booking, exactly one group. The old inbox rendered a confirmed booking
 * for today twice (once as pending, once as today); this split is disjoint by
 * construction.
 */
export function groupAgenda(bookings: Booking[], now: Date): AgendaGroups {
  const visible = bookings
    .filter((booking) => !AGENDA_HIDDEN.has(booking.status))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const porCerrar = visible.filter(
    (booking) => booking.status === 'confirmed' && new Date(booking.startsAt) <= now,
  );
  const rest = visible.filter((booking) => !porCerrar.includes(booking));
  const hoy = rest.filter((booking) => isSameDay(new Date(booking.startsAt), now));
  const future = rest.filter(
    (booking) => !isSameDay(new Date(booking.startsAt), now) && new Date(booking.startsAt) > now,
  );

  const proximas: AgendaDay[] = [];
  for (const booking of future) {
    const key = localDayKey(new Date(booking.startsAt));
    const day = proximas.find((entry) => entry.key === key);
    if (day) day.bookings.push(booking);
    else proximas.push({ key, bookings: [booking] });
  }
  return { porCerrar, hoy, proximas };
}

/** 'Mañana' for tomorrow, otherwise the weekday and date in the app's voice. */
export function formatAgendaDayLabel(date: Date, now: Date): string {
  const tomorrow = new Date(now.getTime());
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(date, tomorrow)) return 'Mañana';
  // 'viernes, 7 de agosto' reads as a sentence; as a section label the comma
  // is noise, so it goes.
  return date
    .toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' })
    .replace(',', '');
}

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const TO_CHARGE = new Set<BookingStatus>(['confirmed', 'completed']);

/**
 * The visits still to charge at the counter: booked or already arrived, from
 * the last week up to the end of today. Today's come first by time, then the
 * earlier ones, most recent first.
 */
export function bookingsToCharge(bookings: Booking[], now: Date): Booking[] {
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const from = todayStart.getTime() - CHARGE_LOOKBACK_DAYS * 86_400_000;
  const to = todayStart.getTime() + 86_400_000;
  const open = bookings.filter((booking) => {
    const starts = Date.parse(booking.startsAt);
    return TO_CHARGE.has(booking.status) && starts >= from && starts < to;
  });
  const today = open
    .filter((booking) => isSameDay(new Date(booking.startsAt), now))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const earlier = open
    .filter((booking) => !isSameDay(new Date(booking.startsAt), now))
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  return [...today, ...earlier];
}

/** Name-first search for the counter: accents and case never matter, the service name counts too. */
export function matchesBookingSearch(booking: Booking, query: string, service: string): boolean {
  const needle = fold(query.trim());
  if (!needle) return true;
  return fold(`${booking.customerName ?? ''} ${service}`).includes(needle);
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
